import React, { useEffect, useMemo, useState } from 'react';
import { load_takedown_sla_metrics, load_takedowns_list } from '../../services';
import './TakedownSlaWidget.scss';

/**
 * Takedown SLA Widget — directly answers CEPI RFP item:
 *   "Takedown enablement (initiate <= 24h, average <= 5 days)"
 *
 * UX rules:
 *   • In-flight cases (deadline still in the future, not yet filed) render
 *     in neutral tone — they have NOT failed yet, so no red.
 *   • Each stat tile carries an inline 30-day sparkline + a delta vs the
 *     prior 30-day window so a reviewer sees direction, not just snapshot.
 *   • Click any tile to drill into the underlying takedowns list, filtered
 *     to the relevant bucket (met / pending / breached). Esc / backdrop
 *     closes the drawer.
 *   • Sub-minute medians render as "Ns", not "0.Nm".
 */

function unwrap(resp) {
    return resp?.statusCode?.response_data?.data
        ?? resp?.statusCode
        ?? resp?.data
        ?? resp
        ?? {};
}

function fmtPct(v) {
    if (v == null) return '—';
    return `${Number(v).toFixed(0)}%`;
}

function fmtDuration(seconds) {
    if (seconds == null) return '—';
    if (seconds < 60)    return `${Math.round(seconds)}s`;
    if (seconds < 3600)  return `${Math.round(seconds / 60)}m`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
    return `${(seconds / 86400).toFixed(1)}d`;
}

function fmtHours(h) {
    if (h == null) return '—';
    if (h < 1)  return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
}

function deltaArrow(current, prior) {
    if (current == null || prior == null) return null;
    const diff = current - prior;
    if (Math.abs(diff) < 0.1) return { dir: 'flat', txt: '0', tone: 'neutral' };
    const tone = diff > 0 ? 'pos' : 'neg';
    const dir  = diff > 0 ? 'up'  : 'down';
    return { dir, txt: (diff > 0 ? '+' : '') + diff.toFixed(0), tone };
}

// "0% in red on 1 open case" is wrong when nothing has breached yet.
// The classifier needs to know how many ARE breached, not just whether
// the percentage is below the target.
function classify({ met, breached, pending, target }) {
    const evaluable = met + breached;
    if (evaluable === 0) {
        return pending > 0 ? 'pending' : 'neutral';
    }
    const pct = (met / evaluable) * 100;
    if (pct >= target) return 'ok';
    if (pct >= target - 15) return 'warn';
    return 'bad';
}

function Sparkline({ data, accessor, color }) {
    if (!data || data.length === 0) return null;
    const w = 100, h = 24, pad = 1;
    const values = data.map(accessor);
    const max = Math.max(1, ...values);
    const x = i => pad + (i * (w - 2 * pad)) / Math.max(1, values.length - 1);
    const y = v => h - pad - ((v / max) * (h - 2 * pad));
    const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
    return (
        <svg className='tdsla__spark' width={w} height={h} viewBox={`0 0 ${w} ${h}`}
             aria-hidden='true'>
            <path d={path} fill='none' stroke={color} strokeWidth='1.4'
                  strokeLinejoin='round' strokeLinecap='round' />
        </svg>
    );
}

function Delta({ current, prior, suffix = '' }) {
    const d = deltaArrow(current, prior);
    if (!d) return null;
    const symbol = d.dir === 'up' ? '▲' : d.dir === 'down' ? '▼' : '·';
    return (
        <span className={`tdsla__delta tdsla__delta--${d.tone}`}
              title={`Prior period: ${prior == null ? '—' : prior + suffix}`}>
            {symbol} {d.txt}{suffix}
        </span>
    );
}

function StatTile({
    tone, value, label, detail, sparkline, sparkColor,
    delta, onClick, disabled,
}) {
    const clickable = !!onClick && !disabled;
    return (
        <button type='button'
                onClick={clickable ? onClick : undefined}
                disabled={disabled}
                className={`tdsla__stat tdsla__stat--${tone}`
                           + (clickable ? ' tdsla__stat--clickable' : '')}
                aria-label={clickable ? `${label} — view details` : label}>
            <div className='tdsla__stat-headline'>
                <div className='tdsla__stat-value'>{value}</div>
                {sparkline}
            </div>
            <div className='tdsla__stat-label'>{label}</div>
            <div className='tdsla__stat-footer'>
                <span className='tdsla__stat-detail'>{detail}</span>
                {delta}
            </div>
        </button>
    );
}

function TakedownsDrawer({ bucket, label, onClose }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        load_takedowns_list({ bucket: bucket || '', limit: 100 })
            .then(r => {
                if (!alive) return;
                const body = unwrap(r);
                setItems(body.items || []);
                setTotal(body.total || 0);
            })
            .catch(() => alive && setItems([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [bucket]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className='tdsla__overlay' onClick={onClose}>
            <div className='tdsla__drawer' onClick={e => e.stopPropagation()}
                 role='dialog' aria-label={`Takedowns — ${label}`}>
                <div className='tdsla__drawer-head'>
                    <div>
                        <h4>{label}</h4>
                        <p>{total} takedown{total === 1 ? '' : 's'}</p>
                    </div>
                    <button onClick={onClose} className='tdsla__drawer-close'
                            aria-label='Close'>×</button>
                </div>
                <div className='tdsla__drawer-body'>
                    {loading && <div className='tdsla__drawer-empty'>Loading…</div>}
                    {!loading && items.length === 0 && (
                        <div className='tdsla__drawer-empty'>
                            No takedowns in this bucket yet.
                        </div>
                    )}
                    {!loading && items.map(it => {
                        const ch = it.channel_summary || {};
                        return (
                            <div key={it.id} className='tdsla__row-card'>
                                <div className='tdsla__row-card-head'>
                                    <span className='tdsla__row-domain'>{it.domain}</span>
                                    <span className={`tdsla__row-status tdsla__row-status--${it.status}`}>
                                        {it.status}
                                    </span>
                                </div>
                                <div className='tdsla__row-timeline'>
                                    <span><b>Created</b> {it.created_date ? new Date(it.created_date).toLocaleString() : '—'}</span>
                                    <span><b>Filed</b> {it.initiated_at ? new Date(it.initiated_at).toLocaleString() : '—'}</span>
                                    <span><b>Resolved</b> {it.resolved_at ? new Date(it.resolved_at).toLocaleString() : '—'}</span>
                                </div>
                                <div className='tdsla__row-meta'>
                                    <span>
                                        <b>Time-to-file</b> {fmtDuration(it.time_to_initiate_seconds)}
                                        {it.sla_initiate_met === true  && <em className='tdsla__row-met'> · in SLA</em>}
                                        {it.sla_initiate_met === false && <em className='tdsla__row-breached'> · breached</em>}
                                    </span>
                                    <span>
                                        <b>Channels</b> {ch.total || 0}
                                        {ch.accepted  > 0 && <em className='tdsla__row-met'> · {ch.accepted} accepted</em>}
                                        {ch.submitted > 0 && <em> · {ch.submitted} pending</em>}
                                        {ch.failed    > 0 && <em className='tdsla__row-breached'> · {ch.failed} failed</em>}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function TakedownSlaWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState(null);   // { bucket, label }

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await load_takedown_sla_metrics({ days: 30 });
                if (!alive) return;
                setData(unwrap(resp));
            } catch (e) {
                if (alive) setData(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const sparkInitiated = useMemo(
        () => (data?.sparkline || []).map(d => ({ v: d.initiated || 0 })),
        [data],
    );
    const sparkResolved = useMemo(
        () => (data?.sparkline || []).map(d => ({ v: d.resolved || 0 })),
        [data],
    );

    if (loading) return null;
    if (!data || (data.total_takedowns ?? 0) === 0) return null;

    const initiateTone = classify({
        met: data.initiated_within_24h_count || 0,
        breached: data.breached_initiate_count || 0,
        pending: data.pending_initiate_count || 0,
        target: 90,
    });
    const resolveTone = classify({
        met: data.resolved_within_5d_count || 0,
        breached: data.breached_resolve_count || 0,
        pending: data.pending_resolve_count || 0,
        target: 80,
    });

    const prior = data.prior_period || {};

    const initiateDetail = data.pending_initiate_count > 0
        ? `${data.initiated_within_24h_count || 0} in SLA · ${data.pending_initiate_count} in window · ${data.breached_initiate_count || 0} breached`
        : `${data.initiated_within_24h_count || 0} of ${(data.initiated_within_24h_count || 0) + (data.breached_initiate_count || 0)} closed`;

    const resolveDetail = data.pending_resolve_count > 0
        ? `${data.resolved_within_5d_count || 0} in SLA · ${data.pending_resolve_count} in window · ${data.breached_resolve_count || 0} breached`
        : `${data.resolved_within_5d_count || 0} of ${data.total_resolved || 0} closed`;

    return (
        <>
        <div className='tdsla'>
            <div className='tdsla__head'>
                <div>
                    <h4 className='tdsla__title'>Takedown SLA</h4>
                    <p className='tdsla__subtitle'>
                        Initiate ≤ 24h · Resolve ≤ 5d ·
                        last {data.window_days || 30}d window
                    </p>
                </div>
                <button className='tdsla__total tdsla__total--link'
                        onClick={() => setDrawer({ bucket: '', label: 'All takedowns' })}>
                    {data.total_takedowns} total · {data.total_resolved ?? 0} resolved →
                </button>
            </div>

            <div className='tdsla__row'>
                <StatTile
                    tone={initiateTone}
                    value={initiateTone === 'pending' && data.sla_initiate_pct == null
                        ? `${data.pending_initiate_count} in-flight`
                        : fmtPct(data.sla_initiate_pct)}
                    label='Initiated within 24h'
                    detail={initiateDetail}
                    sparkline={<Sparkline data={sparkInitiated} accessor={d => d.v} color='#3b82f6' />}
                    delta={<Delta current={data.sla_initiate_pct}
                                  prior={prior.sla_initiate_pct} suffix='%' />}
                    onClick={() => setDrawer({
                        bucket: data.pending_initiate_count > 0 ? 'pending_initiate' : '',
                        label: data.pending_initiate_count > 0
                            ? `Open takedowns within 24h window`
                            : `All takedowns`,
                    })}
                />

                <StatTile
                    tone={resolveTone}
                    value={resolveTone === 'pending' && data.sla_resolve_pct == null
                        ? `${data.pending_resolve_count} in-flight`
                        : fmtPct(data.sla_resolve_pct)}
                    label='Resolved within 5 days'
                    detail={resolveDetail}
                    sparkline={<Sparkline data={sparkResolved} accessor={d => d.v} color='#16a34a' />}
                    delta={<Delta current={data.sla_resolve_pct}
                                  prior={prior.sla_resolve_pct} suffix='%' />}
                    onClick={() => setDrawer({
                        bucket: data.pending_resolve_count > 0 ? 'pending_resolve' : '',
                        label: data.pending_resolve_count > 0
                            ? `Open takedowns within 5-day window`
                            : `All takedowns`,
                    })}
                />

                <StatTile
                    tone='neutral'
                    value={fmtHours(data.avg_resolve_hours)}
                    label='Average resolve time'
                    detail={`across ${data.total_resolved || 0} closed`}
                    sparkline={null}
                    delta={(() => {
                        const d = deltaArrow(data.avg_resolve_hours, prior.avg_resolve_hours);
                        if (!d) return null;
                        // Lower resolve time is better — invert tone semantics.
                        const tone = d.dir === 'down' ? 'pos' : d.dir === 'up' ? 'neg' : 'neutral';
                        const symbol = d.dir === 'up' ? '▲' : d.dir === 'down' ? '▼' : '·';
                        return (
                            <span className={`tdsla__delta tdsla__delta--${tone}`}
                                  title={`Prior period: ${prior.avg_resolve_hours == null ? '—' : prior.avg_resolve_hours + 'h'}`}>
                                {symbol} {d.txt}h
                            </span>
                        );
                    })()}
                    onClick={data.total_resolved > 0
                        ? () => setDrawer({ bucket: 'met_resolve', label: 'Resolved takedowns' })
                        : null}
                    disabled={data.total_resolved === 0}
                />

                {data.median_time_to_initiate_seconds != null && (
                    <StatTile
                        tone='ok'
                        value={fmtDuration(data.median_time_to_initiate_seconds)}
                        label='Median time to block'
                        detail='auto-submitted across 7 channels'
                        sparkline={null}
                        delta={(() => {
                            const cur = data.median_time_to_initiate_seconds;
                            const pr  = prior.median_time_to_initiate_seconds;
                            if (cur == null || pr == null) return null;
                            const diff = cur - pr;
                            if (Math.abs(diff) < 1) {
                                return <span className='tdsla__delta tdsla__delta--neutral'>· 0s</span>;
                            }
                            const tone = diff < 0 ? 'pos' : 'neg';
                            const symbol = diff > 0 ? '▲' : '▼';
                            return (
                                <span className={`tdsla__delta tdsla__delta--${tone}`}
                                      title={`Prior period: ${fmtDuration(pr)}`}>
                                    {symbol} {fmtDuration(Math.abs(diff))}
                                </span>
                            );
                        })()}
                        onClick={() => setDrawer({
                            bucket: 'met_initiate',
                            label: 'Takedowns filed in SLA',
                        })}
                    />
                )}
            </div>
        </div>

        {drawer && (
            <TakedownsDrawer
                bucket={drawer.bucket}
                label={drawer.label}
                onClose={() => setDrawer(null)}
            />
        )}
        </>
    );
}

export default TakedownSlaWidget;
